import { A11yModule } from '@angular/cdk/a11y';
import { AsyncPipe, LowerCasePipe, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { TranslateModule } from '@ngx-translate/core';
import { FilterPipe } from '@shared/pipes/filter.pipe';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { ISiteListDetails } from '@shared/shared/interfaces/site-settings.interfaces';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { isMobile } from '@shared/utils/platform';
import { BehaviorSubject, Observable, Subject, of, take, takeUntil } from 'rxjs';

@Component({
  selector: 'ten-country-selector',
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    A11yModule,
    NgIf,
    AccessibleClickDirective,
    MatIconModule,
    MatDividerModule,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    FormsModule,
    AsyncPipe,
    LowerCasePipe,
    NgFor,
    FilterPipe,
    MatLegacyButtonModule,
    TranslateModule,
  ],
})
export class CountrySelectorComponent implements OnInit, OnDestroy {
  @Input() public init: Observable<ISiteListDetails>;
  @Output() public emitSiteDetails = new EventEmitter();
  @Output() public closed = new EventEmitter();
  public sites: Observable<ISiteListDetails[]>;
  public selected: BehaviorSubject<ISiteListDetails> = new BehaviorSubject(null);
  public onDestroy$ = new Subject<void>();
  public searchText: string;
  public isMobile: boolean;
  public showGlobeIcon = false;

  @HostListener('window:resize', ['$event'])
  public onResize() {
    this.isMobile = isMobile();
  }

  constructor(
    private siteConfService: SiteConfigService,
    private siteSettingsService: SiteSettingsService
  ) {
    this.isMobile = isMobile();
  }

  public ngOnInit() {
    this.sites = this.siteConfService.siteList;
    this.showGlobeIcon = this.siteConfService.getComponentData('header')?.default?.showGlobeNotCountryFlag;
    this.init.pipe(take(1)).subscribe((res) => this.selected.next(res));
    this.selected.pipe(takeUntil(this.onDestroy$)).subscribe((site) => {
      this.init = of(site);
    });
  }

  public filterSite = (item: ISiteListDetails, type: string) => {
    if (!type || (type && item.display_name?.toLowerCase().includes(type?.toLowerCase()))) {
      return true;
    }
  };

  public tempSite(site) {
    event.stopPropagation();
    this.selected.next(site);
  }

  public setSite(selectedSite?) {
    let site = selectedSite;
    if (this.isMobile && !selectedSite) {
      this.selected.pipe(takeUntil(this.onDestroy$)).subscribe((res) => {
        site = res;
      });
    }
    this.selected.next(site);
    this.emitSiteDetails.emit(site);
    this.redirectSite(site);
    localStorage.removeItem('scrollRestoreData');
  }

  private redirectSite(site) {
    localStorage.setItem('selectedCountry', JSON.stringify(site));
    const country = site.country_code?.toLowerCase();

    if (window.location.href.includes('mastercard') && country === 'us') {
      window.location.href = 'https://travel.mastercard.us';
      return;
    }

    const lang = site.language;
    const feConfigPath = this.siteSettingsService?.settings?.fe_configuration?.path;
    let returnUrl = '/';

    if (this.siteConfService.getComponentData('selector')?.default?.setCountryInURL) {
      let brandPath = this.siteSettingsService.replaceBrandPath(feConfigPath);
      brandPath = this.siteSettingsService.brandPathOverride(brandPath, country);
      returnUrl = `/product/${brandPath}/homepage/${country}/${lang}`;
    }
    this.siteSettingsService.syncSiteId(site?.site_id);
    window.location.href = returnUrl;
  }

  public onCancel() {
    this.closed.emit(true);
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
